import { useSyncExternalStore } from "react";
import { getMarket, isBlocked, isCrawler } from "./market";
import { smartBannerContent } from "./storeLinks";

/*
 * "Get the app" bridges for phone visitors:
 *
 * - iOS: Safari's own Smart App Banner, from <meta name="apple-itunes-app">.
 *   public/index.html carries India's app as the default; prerendered pages
 *   get their store's app and their own URL (scripts/prerender-meta.js), and
 *   syncSmartBanner() keeps it right for the store and page at runtime.
 * - Android: a slim, dismissible bar at the top of the page
 *   (src/components/AppBanner). Never full-screen or an interstitial.
 *
 * Both show only on the pages below (bannerPageType): never on cart,
 * checkout, payment verification or sign-in, always on the order pages after
 * a purchase.
 */

export const PAGE_TYPES = {
  HOME: "home",
  BRAND: "brand",
  CATEGORY: "category",
  ORDER: "order",
  GIFT: "gift",
};

/**
 * The page type a path gets the banner on (also the banner's utm_campaign),
 * or null for pages that never show it: cart, checkout,
 * /order-confirmation (the payment is still being verified there), sign-in,
 * account, support and anything unlisted.
 */
export const bannerPageType = (pathname) => {
  const p = String(pathname || "/").replace(/\/+$/, "") || "/";
  if (p === "/" || p === "/marketplace" || /^\/marketplace\/uniquely-available-plans\/[^/]+$/.test(p)) return PAGE_TYPES.HOME;
  if (/^\/brand\/[^/]+$/.test(p) || /^\/[a-z]{2}\/brand\/[^/]+$/i.test(p) || /^\/product\/[^/]+$/.test(p)) return PAGE_TYPES.BRAND;
  if (/^\/category\/[^/]+$/.test(p)) return PAGE_TYPES.CATEGORY;
  if (p === "/order" || p === "/myorder" || /^\/orderdetail\/[^/]+$/.test(p)) return PAGE_TYPES.ORDER;
  if (/^\/gift\/[^/]+$/.test(p)) return PAGE_TYPES.GIFT;
  return null;
};

const browser = () => {
  if (typeof navigator === "undefined") return {};
  return { userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints };
};

/**
 * "android", "ios" or "other". iPadOS Safari reports itself as a Mac; a Mac
 * with a touch screen is an iPad.
 */
export const platformOf = ({ userAgent = "", platform = "", maxTouchPoints = 0 } = browser()) => {
  const ua = String(userAgent || "").toLowerCase();
  if (ua.includes("android")) return "android";
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (platform === "MacIntel" && maxTouchPoints > 1) return "ios";
  return "other";
};

/**
 * An Android WebView rather than a browser: the "; wv)" token every WebView
 * since Android 5 sends. Covers apps that open pages inside themselves
 * (Facebook, Instagram and other in-app browsers), where a store link may not
 * reach Play. (Fleek's own app never shows these pages: its payment WebView
 * stops at the return URL.)
 */
export const isAndroidWebView = (userAgent = browser().userAgent) => /;\s*wv\)/i.test(String(userAgent || ""));

/** A browser on an Android phone or tablet where the Play Store can open. */
export const canShowAndroidBanner = (info = browser()) =>
  platformOf(info) === "android" && !isAndroidWebView(info.userAgent) && !isCrawler(info.userAgent || "");

// ---- Dismissal: remembered for 30 days on this browser ----

export const DISMISS_KEY = "fleek_app_banner_dismissed_at";
export const DISMISS_DAYS = 30;
const DISMISS_MS = DISMISS_DAYS * 24 * 60 * 60 * 1000;

// Also kept in memory, so a dismissal holds for the visit when storage is
// blocked (private mode, disabled site data).
let dismissedThisVisit = false;

export const isDismissed = (now = Date.now()) => {
  if (dismissedThisVisit) return true;
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && now - at < DISMISS_MS;
  } catch (e) {
    return false;
  }
};

export const rememberDismissal = (now = Date.now()) => {
  dismissedThisVisit = true;
  try {
    localStorage.setItem(DISMISS_KEY, String(now));
  } catch (e) {
    // Storage blocked: the in-memory flag covers this visit.
  }
};

/** Tests only: forget the in-memory dismissal. */
export const resetDismissalForTests = () => {
  dismissedThisVisit = false;
};

// ---- Whether the Android bar is on screen, for pages with an app section of their own ----

let barShowing = false;
const listeners = new Set();

export const setAppBarShowing = (value) => {
  if (barShowing === !!value) return;
  barShowing = !!value;
  listeners.forEach((fn) => fn());
};

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** True while the Android "get the app" bar is showing. */
export const useAppBarShowing = () => useSyncExternalStore(subscribe, () => barShowing, () => false);

// ---- iOS Smart App Banner ----

const META_SELECTOR = 'meta[name="apple-itunes-app"]';

/**
 * The apple-itunes-app content for a page, or null where it must not show
 * (pages without a banner, blocked countries).
 */
export const smartBannerFor = ({ market, blocked = false, pathname, url }) => {
  if (blocked || !bannerPageType(pathname)) return null;
  return smartBannerContent(market, url);
};

/** Write (or, for null, remove) the apple-itunes-app meta in <head>. */
export const applySmartBanner = (content, doc = document) => {
  try {
    const existing = doc.head.querySelector(META_SELECTOR);
    if (!content) {
      if (existing) existing.remove();
      return;
    }
    const el = existing || doc.createElement("meta");
    el.setAttribute("name", "apple-itunes-app");
    el.setAttribute("content", content);
    if (!existing) doc.head.appendChild(el);
  } catch (e) {
    // Never let the banner break a page.
  }
};

/**
 * Bring the Smart App Banner in line with the store and page: the store's
 * app, this page's URL as the app-argument. Run at start-up (before the
 * document finishes loading, which is when Safari reads it) and on every
 * route change.
 */
export const syncSmartBanner = (loc = window.location) => {
  const url = `${loc.origin}${loc.pathname}${loc.search || ""}`;
  applySmartBanner(smartBannerFor({ market: getMarket(), blocked: isBlocked(), pathname: loc.pathname, url }));
};
